import { IonIcon, IonSpinner } from '@ionic/react';
import {
  alertCircleOutline,
  checkmarkOutline,
  layersOutline,
  playForwardOutline,
} from 'ionicons/icons';
import React, { useEffect, useMemo } from 'react';
import { TrackFacts } from '../duplicateVerdict';
import { DuplicatePlan } from '../useDuplicatePlan';
import CandidateRow, { VaryingFacts } from './CandidateRow';

type Props = {
  plan: DuplicatePlan;
  busy: boolean;
};

/** Which facts actually differ, so the row chips can point at what matters. */
const findVaryingFacts = (facts: TrackFacts[]): VaryingFacts => {
  const varying: VaryingFacts = new Set();
  const distinct = <T,>(read: (f: TrackFacts) => T) =>
    new Set(facts.map(read)).size > 1;

  if (distinct((f) => `${f.extension}|${f.bitrate ?? ''}`))
    varying.add('format');
  if (distinct((f) => Math.round(f.duration ?? 0))) varying.add('length');
  if (distinct((f) => f.cues)) varying.add('cues');
  if (distinct((f) => f.loops)) varying.add('loops');
  if (distinct((f) => f.playCount)) varying.add('plays');
  if (distinct((f) => f.crateCount)) varying.add('crates');
  if (distinct((f) => f.isDrm)) varying.add('drm');

  return varying;
};

/**
 * The sets we couldn't call, one at a time. The reason it needs a human comes
 * before the choice, so the user knows what they are actually deciding.
 */
const ReviewStage: React.FC<Props> = ({ plan, busy }) => {
  const entry = plan.currentEntry;
  const facts = useMemo(() => entry?.verdict.facts ?? [], [entry]);
  const varying = useMemo(() => findVaryingFacts(facts), [facts]);

  const total = plan.reviewEntries.length;
  const position = Math.min(plan.reviewIndex + 1, total);

  useEffect(() => {
    if (!entry || busy) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const digit = Number(event.key);
      if (Number.isInteger(digit) && digit >= 1 && digit <= facts.length) {
        plan.chooseKeeper(entry.group.id, facts[digit - 1].track.id);
        return;
      }
      if (event.key === 'Enter') plan.confirmCurrent();
      if (event.key.toLowerCase() === 's') plan.skipCurrent();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, entry, facts, plan]);

  if (!entry) {
    return (
      <div className="dup-stage dup-review">
        <div className="dup-review-empty">
          <h2>Nothing left to review</h2>
          <p>
            {plan.skippedCount > 0
              ? `You skipped ${plan.skippedCount} set${
                  plan.skippedCount === 1 ? '' : 's'
                }. They stay in your library untouched.`
              : 'Every set has been decided.'}
          </p>
          <button
            type="button"
            className="dup-btn dup-btn--primary"
            onClick={plan.finishReview}
          >
            See the summary
          </button>
        </div>
      </div>
    );
  }

  const keeperId = entry.verdict.keeperId;
  const removeCount = entry.verdict.removeIds.length;
  const removingFromCrates = facts.filter(
    (fact) =>
      entry.verdict.removeIds.includes(fact.track.id) && fact.crateCount > 0,
  );

  return (
    <div className="dup-stage dup-review">
      <header className="dup-review-head">
        <div className="dup-review-progress">
          <span className="dup-eyebrow">
            Set <span className="dup-num">{position}</span> of{' '}
            <span className="dup-num">{total}</span>
          </span>
          <div className="dup-progress-track" aria-hidden="true">
            <div
              className="dup-progress-fill"
              style={{ width: `${(position / Math.max(total, 1)) * 100}%` }}
            />
          </div>
        </div>
        <h1>{entry.group.name}</h1>
      </header>

      {entry.verdict.questions.length > 0 && (
        <section className="dup-questions">
          <h2>
            <IonIcon icon={alertCircleOutline} />
            Why I&apos;m asking you
          </h2>
          <ul>
            {entry.verdict.questions.map((question) => (
              <li key={question.kind}>{question.text}</li>
            ))}
          </ul>
        </section>
      )}

      <p className="dup-review-instruction">
        Pick the copy you want to keep. The rest go to your Recycle Bin.
      </p>

      <div className="dup-candidates">
        {facts.map((fact, index) => (
          <CandidateRow
            key={fact.track.id}
            facts={fact}
            setName={`keeper-${entry.group.id}`}
            index={index}
            isKeeper={fact.track.id === keeperId}
            isRecommended={index === 0}
            recommendation={entry.verdict.headline}
            varying={varying}
            onChoose={() => plan.chooseKeeper(entry.group.id, fact.track.id)}
          />
        ))}
      </div>

      {plan.crateIndexAvailable && removingFromCrates.length > 0 && (
        <p className="dup-serato-note">
          <IonIcon icon={layersOutline} />
          Removing {removingFromCrates.length === 1
            ? 'a copy'
            : 'copies'} from{' '}
          {removingFromCrates.reduce((sum, f) => sum + f.crateCount, 0)} Serato
          crate reference
          {removingFromCrates.reduce((sum, f) => sum + f.crateCount, 0) === 1
            ? ''
            : 's'}
          . Serato will point those crates at the copy you keep.
        </p>
      )}

      <footer className="dup-review-actions">
        <button
          type="button"
          className="dup-btn dup-btn--primary"
          onClick={plan.confirmCurrent}
          disabled={busy}
        >
          {busy ? (
            <IonSpinner name="dots" />
          ) : (
            <>
              <IonIcon icon={checkmarkOutline} />
              Keep this, remove the other{' '}
              {removeCount === 1 ? 'copy' : removeCount}
            </>
          )}
        </button>
        <button
          type="button"
          className="dup-btn dup-btn--quiet"
          onClick={plan.skipCurrent}
          disabled={busy}
        >
          <IonIcon icon={playForwardOutline} />
          Skip for now
        </button>
        <button
          type="button"
          className="dup-btn dup-btn--quiet"
          onClick={plan.keepAllInCurrent}
          disabled={busy}
        >
          <IonIcon icon={layersOutline} />
          They&apos;re all different — keep them
        </button>

        <p className="dup-keys">
          <kbd>1</kbd>–<kbd>{facts.length}</kbd> choose · <kbd>Enter</kbd>{' '}
          confirm · <kbd>S</kbd> skip
        </p>
      </footer>
    </div>
  );
};

export default ReviewStage;
